import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { InfiniteGridProps, DEFAULT_GRID_CONFIG } from './types';

/**
 * InfiniteGrid - Web implementation using CSS
 * 
 * Renders an infinite grid pattern using CSS background-image for optimal web performance.
 * Uses CSS transforms and hardware acceleration for smooth panning and zooming.
 * 
 * Performance features:
 * - CSS-based rendering: Leverages browser's native rendering engine
 * - Hardware acceleration: Uses will-change and transform3d
 * - Single background pattern: No DOM elements to manage
 * - GPU compositing: Offloads rendering to GPU
 */
const InfiniteGrid: React.FC<InfiniteGridProps> = ({
  zoomLevel,
  offsetX,
  offsetY,
  viewportWidth,
  viewportHeight,
  cellSize = DEFAULT_GRID_CONFIG.cellSize,
  dotMode = DEFAULT_GRID_CONFIG.dotMode,
  dotRadius = DEFAULT_GRID_CONFIG.dotRadius,
  lineWidth = DEFAULT_GRID_CONFIG.lineWidth,
  color = DEFAULT_GRID_CONFIG.color,
  opacity = DEFAULT_GRID_CONFIG.opacity,
}) => {
  // Performance optimization: Adaptive rendering based on zoom level
  const shouldRenderGrid = useMemo(() => {
    return zoomLevel >= 0.1 && zoomLevel <= 10;
  }, [zoomLevel]);

  // Performance optimization: Adjust cell size at extreme zoom levels
  const adaptiveCellSize = useMemo(() => {
    if (zoomLevel < 0.3) return cellSize * 4;
    if (zoomLevel < 0.5) return cellSize * 2;
    return cellSize;
  }, [zoomLevel, cellSize]);

  // Memoize grid style calculation
  const gridStyle = useMemo(() => {
    if (!shouldRenderGrid) {
      return { display: 'none' };
    }

    // Safety: Validate offset values
    const safeOffsetX = typeof offsetX === 'number' && !isNaN(offsetX) ? offsetX : 0;
    const safeOffsetY = typeof offsetY === 'number' && !isNaN(offsetY) ? offsetY : 0;

    const cellSizeStr = `${adaptiveCellSize}px`;

    // Calculate background position based on offset
    // This creates the "infinite" effect by repositioning the pattern
    const bgPosX = -(safeOffsetX % adaptiveCellSize);
    const bgPosY = -(safeOffsetY % adaptiveCellSize);

    if (dotMode) {
      // DOT MODE: Use radial-gradient for dots
      const adjustedDotRadius = Math.max(dotRadius * 0.5, 1);
      return {
        backgroundImage: `radial-gradient(circle, ${color} ${adjustedDotRadius}px, transparent ${adjustedDotRadius}px)`,
        backgroundSize: `${cellSizeStr} ${cellSizeStr}`,
        backgroundPosition: `${bgPosX}px ${bgPosY}px`,
        opacity,
        // Performance: Hardware acceleration
        willChange: 'transform, background-position',
        transform: 'translate3d(0, 0, 0)', // Force GPU compositing
      };
    } else {
      // LINE MODE: Use linear-gradient for lines
      return {
        backgroundImage: `
          linear-gradient(to right, ${color} ${lineWidth}px, transparent ${lineWidth}px),
          linear-gradient(to bottom, ${color} ${lineWidth}px, transparent ${lineWidth}px)
        `,
        backgroundSize: `${cellSizeStr} ${cellSizeStr}`,
        backgroundPosition: `${bgPosX}px ${bgPosY}px`,
        opacity,
        // Performance: Hardware acceleration
        willChange: 'transform, background-position',
        transform: 'translate3d(0, 0, 0)', // Force GPU compositing
      };
    }
  }, [
    shouldRenderGrid,
    offsetX,
    offsetY,
    adaptiveCellSize,
    dotMode,
    dotRadius,
    lineWidth,
    color,
    opacity,
  ]);

  if (!shouldRenderGrid) {
    return null;
  }

  return (
    <View
      style={[styles.container, gridStyle]}
      pointerEvents="none"
    />
  );
};

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -1, // Behind content
    // Performance: Enable GPU acceleration
    backfaceVisibility: 'hidden',
    perspective: 1000,
  },
});

// Performance optimization: Wrap in React.memo with custom comparison
export default React.memo(
  InfiniteGrid,
  (prevProps, nextProps) => {
    // Only re-render if these specific props change
    return (
      prevProps.offsetX === nextProps.offsetX &&
      prevProps.offsetY === nextProps.offsetY &&
      prevProps.zoomLevel === nextProps.zoomLevel &&
      prevProps.viewportWidth === nextProps.viewportWidth &&
      prevProps.viewportHeight === nextProps.viewportHeight &&
      prevProps.cellSize === nextProps.cellSize &&
      prevProps.dotMode === nextProps.dotMode &&
      prevProps.color === nextProps.color &&
      prevProps.opacity === nextProps.opacity
    );
  }
);

